// 自前アクセス解析の SDK。設計: doc/issues/261001_first_party_analytics_platform.md §クライアント SDK
//
// - 表示時のイベントは fetch(keepalive) で送る。応答を読めるので、届かなかった分を再送待ちに残せる
// - ページを離れるとき・非表示になるときは sendBeacon で送る（応答は読めない）
// - 計測する枠は HTML 属性で印を付ける: data-an-region / data-an-experiment（枠）、data-an-item / data-an-position / data-an-arm（項目）

const ENDPOINT = "/_e"
const SESSION_KEY = "an_session"
const RETRY_KEY = "an_retry"
const SESSION_IDLE_MS = 30 * 60 * 1000
// サーバーの重複照合（直近 7 日）より短くする
const RETRY_TTL_MS = 24 * 60 * 60 * 1000
const RETRY_MAX = 200
// 1 リクエストを /_e の上限（64KB・50 件）に収める。長い URL の event が並んでも超えない件数にする
const BATCH_MAX = 20
// bbsnews の CloudFront の WAF は body が 8KB を超える POST を 403 で Block する。4xx は再送しないので、超えると再送待ちごと消える
const BATCH_BYTES_MAX = 7 * 1024
const IMPRESSION_MS = 1000
const IMPRESSION_RATIO = 0.5
const REACHED_END_PCT = 90

const queue = []
const vitals = {}
const vitalObservers = []
let view = null

// cookie を禁止した環境では localStorage の参照自体が例外になるので、参照も try の中で行う
function readStorage(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key))
  } catch {
    return null
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage が使えなくても計測は続ける
  }
}

// サーバーは UUID でない session_id を捨てるので、storage に残った壊れた値は作り直す
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// crypto.randomUUID は secure context でしか使えないので、getRandomValues で UUID v4 を作る
function uuid() {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// サーバーも path / referrer を 2048 バイトで切る。1 リクエスト 20 件が /_e の上限（64KB）に収まるよう、送る前に切る
const URL_MAX = 2048
const clip = (url) => url?.slice(0, URL_MAX)

// 数でない値は NaN になり、JSON では null になってサーバーが落とす
const toInteger = (value) => Number.parseInt(value, 10)

// ページの id は FSID（64 bit）で Number.MAX_SAFE_INTEGER を超えるので、数値にせず数字の文字列のまま送る
const pageId = (value) => (/^[1-9]\d{0,18}$/.test(value ?? "") ? value : undefined)
const screenId = (value) => (/^[1-9]\d{5}$/.test(value ?? "") ? Number(value) : undefined)

function sessionId() {
  const now = Date.now()
  let session = readStorage(SESSION_KEY)
  if (!UUID.test(session?.id) ||!(now - session.at <= SESSION_IDLE_MS)) session = { id: uuid() }
  session.at = now
  writeStorage(SESSION_KEY, session)
  return session.id
}

// Global Privacy Control の端末は page_view だけを送る（サーバーも visitor_id / user_id を付けず、他は捨てる）
const gpc = navigator.globalPrivacyControl === true

function track(name, props = {}) {
  if (!view || view.ended || (gpc && name !== "page_view")) return

  queue.push({
    name,
    event_id: uuid(),
    occurred_at: new Date().toISOString(),
    session_id: sessionId(),
    view_id: view.id,
    page_id: view.pageId,
    screen_id: view.screenId,
    url: clip(view.url),
    referrer: clip(view.referrer),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    lang: navigator.language,
    props,
  })
}

// ---- 送信と再送

// 拡張機能などで壊れた値が入っていても、捨てて計測を続ける
function storedRetries() {
  const stored = readStorage(RETRY_KEY)
  if (!Array.isArray(stored)) return []

  return stored.filter((event) => typeof event?.occurred_at === "string" && Date.now() - Date.parse(event.occurred_at) < RETRY_TTL_MS)
}

function saveRetries(events) {
  const ids = new Set(events.map((event) => event.event_id))
  const kept = storedRetries().filter((event) => !ids.has(event.event_id)).concat(events)
  // 上限で押し出すのは発生の古い方から。失敗した古い区切りを入れ直しても、まだ送っていない新しいイベントを押し出さない
  kept.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  writeStorage(RETRY_KEY, kept.slice(-RETRY_MAX))
}

function forgetRetries(events) {
  const ids = new Set(events.map((event) => event.event_id))
  writeStorage(RETRY_KEY, storedRetries().filter((event) => !ids.has(event.event_id)))
}

function chunks(events) {
  const encoder = new TextEncoder()
  const result = [[]]
  let bytes = 0
  for (const event of events) {
    const size = encoder.encode(JSON.stringify(event)).length + 1
    const last = result[result.length - 1]
    if (last.length && (last.length >= BATCH_MAX || bytes + size > BATCH_BYTES_MAX)) {
      result.push([event])
      bytes = size
    } else {
      last.push(event)
      bytes += size
    }
  }
  return result
}

// 再送待ちは届いたと分かるまで消さない（送信中にタブを閉じても次の表示で送り直す）。
// サーバーは event_id で重複を畳むので、二重に送っても数は増えない
function send(chunk, { beacon }) {
  // 文字列の body は text/plain になり、sendBeacon の Content-Type の制限に当たらない
  const body = JSON.stringify({ events: chunk })
  if (beacon && navigator.sendBeacon(ENDPOINT, body)) return Promise.resolve()

  return fetch(ENDPOINT, { method: "POST", body, keepalive: true })
    .then((response) => (response.status >= 500 ? saveRetries(chunk) : forgetRetries(chunk)))
    .catch(() => saveRetries(chunk))
}

function post(events, { beacon }) {
  const batches = chunks(events)
  if (beacon) return batches.forEach((chunk) => send(chunk, { beacon }))

  // 表示時は 1 本ずつ応答を待って送る。先の応答で受け取った visitor cookie が次に乗り、cookie を 2 回発行させない。
  // 待っている区切りは keepalive でも守られないので、先に再送待ちへ入れておき、タブを閉じても次の表示で送る
  if (batches.length > 1) saveRetries(batches.slice(1).flat())
  batches.reduce((previous, chunk) => previous.then(() => send(chunk, { beacon })), Promise.resolve())
}

// 表示時は再送待ちと今回の分を 1 回で送る。初回に別々のリクエストを並べると、visitor cookie が 2 回発行される
function flush({ beacon, withRetries = false }) {
  const events = (withRetries ? storedRetries() : []).concat(queue.splice(0))
  if (events.length) post(events, { beacon })
}

// ---- 表示時間とスクロール

function engagedNow() {
  return document.visibilityState === "visible" && document.hasFocus()
}

function tickEngagement() {
  if (!view || view.ended) return

  const now = performance.now()
  if (view.activeSince !== null) view.engagedMs += now - view.activeSince
  view.activeSince = engagedNow() ? now : null
}

function updateScroll() {
  if (!view || view.ended) return

  const documentHeight = document.documentElement.scrollHeight
  const pct = documentHeight ? Math.min(100, Math.round(((window.scrollY + window.innerHeight) / documentHeight) * 100)) : 100
  view.maxScrollPct = Math.max(view.maxScrollPct, pct)
}

// 同じ表示で何度か送ることがある（タブを切り替えて戻ったとき）。engaged_ms は累計なので、集計は view_id ごとの最大値を使う
function trackEngagement() {
  tickEngagement()
  updateScroll()
  track("page_engagement", {
    engaged_ms: Math.round(view.engagedMs),
    max_scroll_pct: view.maxScrollPct,
    reached_end: view.maxScrollPct >= REACHED_END_PCT,
  })
}

// ---- 枠・表示・クリック

function regionProps(region) {
  return { region: region.dataset.anRegion, experiment: region.dataset.anExperiment }
}

function itemProps(element) {
  const item = element.closest("[data-an-item]")
  return {
    ...regionProps(element.closest("[data-an-region]")),
    arm: item?.dataset.anArm,
    position: toInteger(item?.dataset.anPosition),
    item_page_id: pageId(item?.dataset.anItem),
  }
}

// IntersectionObserver の isIntersecting は 1px でも交差すれば true になるので、見えている割合で判定する
function onIntersection(entries) {
  for (const entry of entries) {
    const item = entry.target
    const timers = view.impressionTimers
    if (entry.intersectionRatio >= IMPRESSION_RATIO) {
      if (timers.has(item)) continue

      timers.set(item, setTimeout(() => {
        timers.delete(item)
        // IntersectionObserver はタブの可視性を見ないので、非表示の間に経った 1 秒は数えない。
        // 見えたままの項目は交差が変わらず通知も来ないので、表示に戻ったときに観測し直す
        if (document.visibilityState !== "visible") {
          view.hiddenItems.add(item)
          return
        }

        view.observer.unobserve(item)
        const props = itemProps(item)
        const key = JSON.stringify(props)
        if (view.impressed.has(key)) return

        view.impressed.add(key)
        track("impression", props)
      }, IMPRESSION_MS))
    } else {
      clearTimeout(timers.get(item))
      timers.delete(item)
    }
  }
}

// 遅延読込の turbo-frame の中の枠も、読み込まれた時点で数える。
// 同じ表示で body が描き直される（アンカーから戻る restore の visit）と要素が替わるので、
// 重複は要素ではなく枠の名前と項目の値で見る
function scanRegions(root) {
  if (!view || view.ended) return

  for (const region of root.querySelectorAll("[data-an-region]")) {
    const props = regionProps(region)
    const key = JSON.stringify(props)
    if (!view.regions.has(key)) {
      view.regions.add(key)
      track("slot_eligible", props)
    }
    for (const item of region.querySelectorAll("[data-an-item]")) view.observer.observe(item)
  }
}

// 別のタブ・ウィンドウで開くクリックは、遷移先の表示が prev_view_id でつながらないので、集計で良いクリックの判定から外す
function opensNewTab(event, link) {
  return event.type === "auxclick" || event.ctrlKey || event.metaKey || event.shiftKey || link.target === "_blank"
}

function onClick(event) {
  if (!(event.target instanceof Element)) return

  const link = event.target.closest("[data-an-region] a[href]")
  if (!link) return

  track("click", {
    ...itemProps(link),
    href: clip(link.href),
    internal: link.host === window.location.host,
    new_tab: opensNewTab(event, link),
  })
}

// form.action / form.target は name="action" / "target" の入力欄があるとその要素を返すので、属性から読む
function onSubmit(event) {
  const form = event.target
  if (!(form instanceof HTMLFormElement) || !form.closest("[data-an-region]")) return

  const destination = new URL(form.getAttribute("action") ?? "", window.location.href)
  track("click", {
    ...itemProps(form),
    href: clip(destination.href),
    internal: destination.host === window.location.host,
    new_tab: form.getAttribute("target") === "_blank",
  })
}

function onCopy() {
  const chars = String(window.getSelection() || "").length
  if (chars) track("copy", { chars })
}

// ---- Web Vitals（最初の document だけ。Turbo の遷移では測らない）

function observeVitals() {
  const observe = (type, callback, options = {}) => {
    try {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach(callback))
      observer.observe({ type, buffered: true, ...options })
      vitalObservers.push(observer)
    } catch {
      // 対応していないブラウザでは測らない
    }
  }

  observe("largest-contentful-paint", (entry) => {
    vitals.LCP = entry.startTime
  })
  observe("layout-shift", (entry) => {
    if (!entry.hadRecentInput) vitals.CLS = (vitals.CLS || 0) + entry.value
  })
  // INP は操作ごとの最大の処理時間で近似する（web-vitals ライブラリの 98 パーセンタイルとは違う）
  observe("event", (entry) => {
    if (entry.interactionId) vitals.INP = Math.max(vitals.INP || 0, entry.duration)
  }, { durationThreshold: 40 })

  const navigation = performance.getEntriesByType("navigation")[0]
  if (navigation) vitals.TTFB = navigation.responseStart
}

const roundVital = (metric, value) => (metric === "CLS" ? Math.round(value * 10000) / 10000 : Math.round(value))

// 最初に非表示になったか離れたときに 1 回だけ送り、それ以降は測らない
function trackVitals() {
  if (!vitalObservers.length && !Object.keys(vitals).length) return

  vitalObservers.splice(0).forEach((observer) => observer.disconnect())
  for (const [metric, value] of Object.entries(vitals)) {
    track("web_vitals", { metric, value: roundVital(metric, value) })
    delete vitals[metric]
  }
}

function trackViewSummary() {
  trackEngagement()
  trackVitals()
}

// ---- 表示の始まりと終わり

function endView({ beacon }) {
  if (!view || view.ended) return

  trackViewSummary()
  view.ended = true
  view.observer.disconnect()
  view.impressionTimers.forEach((timer) => clearTimeout(timer))
  flush({ beacon })
}

// SDK を入れない layout（編集画面など）に Turbo で遷移したときは、表示を閉じるだけで数えない
function startView({ restored = false } = {}) {
  const previous = view
  endView({ beacon: false })
  // 次の表示の referrer がこのページになり、prev_view_id が経由前の表示を指さないよう、閉じた表示として置いておく
  if (!document.querySelector('meta[name="an-enabled"]')) {
    view = { url: window.location.href, ended: true }
    return
  }

  view = {
    id: uuid(),
    pageId: pageId(document.querySelector('meta[name="an-page-id"]')?.content),
    screenId: screenId(document.querySelector('meta[name="an-screen-id"]')?.content),
    url: window.location.href,
    // bfcache から戻った表示の前の表示は自分自身なので、referrer にしない
    referrer: restored ? undefined : previous?.url || document.referrer,
    engagedMs: 0,
    activeSince: null,
    maxScrollPct: 0,
    regions: new Set(),
    impressed: new Set(),
    impressionTimers: new Map(),
    hiddenItems: new Set(),
    observer: new IntersectionObserver(onIntersection, { threshold: IMPRESSION_RATIO }),
    ended: false,
  }
  tickEngagement()

  track("page_view", { prev_view_id: previous?.id })
  scanRegions(document)
  updateScroll()
  flush({ beacon: false, withRetries: true })
}

// - tracked 要素が合わず Turbo が reload に切り替えると、離れる URL のまま turbo:load が来る（その後 full load する）
// - 最初の turbo:load がこの module の実行より後に来たときに、同じ表示を 2 回数えない
let reloading = false

// ページ内のアンカーへ移ってから戻ると、同じ document のまま Turbo が restore の visit を走らせる
const sameDocument = (a, b) => a.split("#")[0] === b.split("#")[0]

function onLoad() {
  if (reloading) return
  if (view && !view.ended && sameDocument(view.url, window.location.href)) {
    scanRegions(document)
    return
  }

  startView()
}

function onVisit(event) {
  reloading = false
  if (event.detail.action === "restore" && view && sameDocument(event.detail.url, view.url)) return

  endView({ beacon: true })
}

function onVisibilityChange() {
  if (!view || view.ended) return

  if (document.visibilityState === "hidden") {
    trackViewSummary()
    flush({ beacon: true })
  } else {
    tickEngagement()
    for (const item of view.hiddenItems) {
      view.observer.unobserve(item)
      view.observer.observe(item)
    }
    view.hiddenItems.clear()
  }
}

function start() {
  observeVitals()
  startView()

  // turbo:visit は戻る・進む（before-visit が出ない）でも出て、まだ前の document のうちに来る
  document.addEventListener("turbo:visit", onVisit)
  document.addEventListener("turbo:load", onLoad)
  document.addEventListener("turbo:reload", () => {
    reloading = true
  })
  // advance の frame の遷移では、URL が先に変わってから turbo:frame-load → turbo:visit と進み、表示が替わる。
  // URL が今の表示と違うときは数えない（終わる表示に slot_eligible を付け、新しい表示でもう一度数えることになる）
  document.addEventListener("turbo:frame-load", (event) => {
    if (view && sameDocument(view.url, window.location.href)) scanRegions(event.target)
  })
  document.addEventListener("visibilitychange", onVisibilityChange)
  document.addEventListener("click", onClick, true)
  // 中クリックで新しいタブに開くときは click ではなく auxclick が来る
  document.addEventListener("auxclick", (event) => {
    if (event.button === 1) onClick(event)
  }, true)
  // フォームの handler が submit の伝播を止めても拾えるよう、capture で先に拾う
  document.addEventListener("submit", onSubmit, true)
  document.addEventListener("copy", onCopy)
  window.addEventListener("pagehide", () => endView({ beacon: true }))
  // bfcache から戻った document は module を実行し直さないので、ここで新しい表示を始める
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) startView({ restored: true })
  })
  window.addEventListener("focus", tickEngagement)
  window.addEventListener("blur", tickEngagement)
  window.addEventListener("scroll", updateScroll, { passive: true })
}

// デプロイで digest が変わると、Turbo の head の merge が新しい版の script を足して実行する。
// 同じ document で 2 つ動かさない（新しい版は次の full load から動く）
if (!window.anSdkStarted) {
  window.anSdkStarted = true
  // ページの機能が目録のイベントを送る入口（/game のプレイのログ）
  window.anTrack = track
  // 先読み（prerender）された document は、表示されるまで数えない
  if (document.prerendering) {
    document.addEventListener("prerenderingchange", start, { once: true })
  } else {
    start()
  }
}
